import { Link } from 'react-router-dom';
import { ArrowRight, UserCheck, FileText, CalendarCheck, RefreshCcw, Handshake, Shield, BarChart3 } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwZGVhbCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3NzI0NDAyNjV8MA&ixlib=rb-4.1.0&q=85';
const VENDOR_IMG = 'https://images.unsplash.com/photo-1758519288905-38b7b00c1023?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwZGVhbCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3NzI0NDAyNjV8MA&ixlib=rb-4.1.0&q=85';

const partnerBenefits = [
  { icon: UserCheck, title: 'Pre-Screened Profiles', desc: 'Access our database of pre-vetted IT professionals with verified credentials and technical assessments already completed.' },
  { icon: FileText, title: 'Submission Ready Resumes', desc: 'Receive professionally formatted, client-ready resumes that meet enterprise submission standards.' },
  { icon: CalendarCheck, title: 'Interview Coordination', desc: 'End-to-end interview scheduling and coordination between candidates, vendors, and end clients.' },
  { icon: RefreshCcw, title: 'Replacement Support', desc: 'Guaranteed replacement support with rapid turnaround to ensure project continuity and client satisfaction.' },
];

const whyPartner = [
  { icon: Handshake, title: 'Collaborative Approach', desc: 'We work alongside our vendor partners, not against them. Mutual success drives our partnership model.' },
  { icon: Shield, title: 'Compliance Support', desc: 'Full regulatory compliance assistance including contract management, background checks, and documentation.' },
  { icon: BarChart3, title: 'Volume Opportunities', desc: 'Access to high-volume staffing requirements from our enterprise client base across three countries.' },
];

const VendorPartnership = () => {
  return (
    <div data-testid="vendor-partnership-page">
      <PageHeader caption="Partnership" title="Vendor Partnership" subtitle="Strategic alliances with staffing firms for seamless talent delivery." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="sol-caption mb-4">Partner With Solnity</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Grow Together Through Strategic Partnership</h2>
              <p className="text-[#525252] leading-relaxed mb-4">Solnity's Vendor Partnership Program is designed for staffing firms and recruiting agencies seeking to expand their reach and capabilities. As a Solnity vendor partner, you gain access to a steady stream of enterprise requirements, collaborative support, and a transparent engagement model.</p>
              <p className="text-[#525252] leading-relaxed mb-8">We believe in building long-term, mutually beneficial relationships with our vendor partners. Our dedicated partner management team ensures smooth operations and consistent communication.</p>
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-6 mb-8">
                <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Get in Touch</h4>
                <div className="space-y-2 text-sm text-[#525252]">
                  <p>Email: <a href="mailto:support@solnity.com" className="text-black hover:underline">support@solnity.com</a></p>
                  <p>Phone: <a href="tel:2267998794" className="text-black hover:underline">226-799-8794</a></p>
                  <p>Scarborough, Ontario, M1H 2M9, Canada</p>
                </div>
              </div>
              <Link to="/become-vendor-partner" className="sol-btn-primary text-sm">Become a Partner <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={VENDOR_IMG} alt="Vendor partnership collaboration" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">What You Get</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Partnership Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {partnerBenefits.map((b) => (
              <div key={b.title} data-testid={`vendor-card-${b.title.toLowerCase().replace(/[\s-]+/g, '-')}`} className="sol-service-card bg-white group">
                <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] mb-5 group-hover:border-black group-hover:bg-black transition-all duration-300">
                  <b.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{b.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Why Solnity</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Why Partner With Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyPartner.map((w) => (
              <div key={w.title} className="sol-service-card">
                <w.icon className="w-6 h-6 mb-5 text-black" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{w.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Ready to Partner?</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">Join our growing network of vendor partners and unlock new business opportunities.</p>
          <Link to="/become-vendor-partner" data-testid="vendor-cta-partner" className="sol-btn-inverse text-sm">Become a Vendor Partner <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
};

export default VendorPartnership;
